import React, { useEffect, useId, useState } from 'react';
import { useDeskInPlan } from '../../../hooks/use-desk-in-plan';
import { w } from '../../../../stays/wording';
import styled from '@emotion/styled';
import type { StayCalendar } from '../../../../interfaces/stays';
import { createBlock, reopenDates } from '../../../../api/stays-reservations-client';
import { FormModal } from '../../../modal/form-modal';
import { ConfirmDialog } from '../../../modal/confirm-dialog';
import { AppButton } from '../../../button/app-button';
import { Field, FieldHint, Input, Label } from '../../../form-kit';
import { addDays, daysBetween } from '../../../../function-utils/building-time';
import { palette, radius, spacing, typography } from '../../../../style-utils/theme';
import toast from '../../../../utils/toast';
import { nightsLabel } from '../reservations/reservation-labels';
import { blockLine, blocksInRange, guestName, shortDay, spanText, staysInRange } from './calendar-model';

/**
 * THE MANAGE DATES SHEET (plan S4.10, R-S4.35) — what a finished selection
 * offers. The selection is INCLUSIVE on screen (14 → 16 Oct is three nights)
 * and leaves for the server with an EXCLUSIVE end (end + 1 day).
 *
 *   a live stay in the range → the alert names it and "Close these dates" is
 *     disabled with that reason beside it;
 *   owner blocks in the range → "Re-open these dates" (listed, confirmed);
 *   otherwise → a Reason (default "Owner use") and "Close these dates".
 * The server has the last word: its 409s are shown as they come.
 */

export const REASON_MAX = 120;
export const DEFAULT_REASON = 'Owner use';

const Alert = styled.p`
  margin: 0 0 ${spacing.sm};
  padding: ${spacing.sm};
  border: 1px solid ${palette.alert};
  border-radius: ${radius.sm};
  background: ${palette.alertSoft};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.alertDeep};
  overflow-wrap: anywhere;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  margin-bottom: ${spacing.sm};
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

const BlockList = styled.ul`
  margin: 0;
  padding-left: ${spacing.md};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.inkSoft};
  overflow-wrap: anywhere;
`;

const Reason = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  line-height: ${typography.leading.snug};
  color: ${palette.slate};
  overflow-wrap: anywhere;
`;

export interface ManageRange {
  /** Inclusive. */
  start: string;
  end: string;
}

export interface ManageDatesSheetProps {
  range: ManageRange | null;
  listingId: string;
  data: StayCalendar;
  onClose: () => void;
  /** After a successful close or re-open: refetch and close. */
  onDone: () => void;
}

const errorText = (e: unknown, fallback: string): string =>
  e instanceof Error && e.message ? e.message : fallback;

export const sheetTitle = (range: ManageRange): string => {
  const nights = daysBetween(range.start, range.end) + 1;

  return `${shortDay(range.start)} → ${shortDay(range.end)} · ${nightsLabel(nights)}`;
};

export const ManageDatesSheet: React.FC<ManageDatesSheetProps> = ({ range, listingId, data, onClose, onDone }) => {
  const [reason, setReason] = useState(DEFAULT_REASON);
  const desk = useDeskInPlan();
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const alertId = useId();
  const reasonId = useId();
  const hintId = useId();

  const rangeKey = range ? `${range.start}|${range.end}` : '';
  useEffect(() => {
    setReason(DEFAULT_REASON);
    setError(null);
    setSaving(false);
    setConfirming(false);
  }, [rangeKey]);

  const endExclusive = range ? addDays(range.end, 1) : '';
  const booked = range ? staysInRange(data, range.start, endExclusive) : [];
  const owned = range ? blocksInRange(data, range.start, endExclusive, 'owner') : [];
  const bookedText = booked.length
    ? `Part of this range is booked (${booked.map(s => `${guestName(s)}, ${spanText(s.check_in, s.check_out)}`).join('; ')})`
    : '';

  const close = async () => {
    if (!range || booked.length || saving) return;
    setSaving(true);
    setError(null);
    try {
      const note = reason.trim();
      await createBlock(listingId, { start_date: range.start, end_date: endExclusive, ...(note ? { note } : {}) });
      toast.success('Dates closed.');
      onDone();
    } catch (e) {
      setError(errorText(e, 'Could not close these dates.'));
    } finally {
      setSaving(false);
    }
  };

  const reopen = async () => {
    if (!range) return;
    setError(null);
    try {
      await reopenDates(listingId, { start_date: range.start, end_date: endExclusive });
      setConfirming(false);
      toast.success('Dates re-opened.');
      onDone();
    } catch (e) {
      setConfirming(false);
      setError(errorText(e, 'Could not re-open these dates.'));
    }
  };

  const footer = range ? (
    <>
      <AppButton variant="ghost" onClick={onClose} disabled={saving}>
        Cancel
      </AppButton>
      {owned.length ? (
        <AppButton variant="secondary" onClick={() => setConfirming(true)}>
          Re-open these dates
        </AppButton>
      ) : (
        <AppButton
          variant="primary"
          disabled={booked.length > 0}
          aria-describedby={booked.length ? alertId : undefined}
          loading={saving}
          loadingLabel="Closing dates"
          onClick={() => void close()}
        >
          Close these dates
        </AppButton>
      )}
    </>
  ) : null;

  return (
    <>
      <FormModal
        open={!!range}
        title={range ? sheetTitle(range) : 'Manage dates'}
        subtitle={`${w('buildingDates', desk)}. The last night shown is the last night closed.`}
        onClose={onClose}
        saving={saving}
        footer={footer}
      >
        {range ? (
          <div data-manage-dates={`${range.start}|${endExclusive}`}>
            {booked.length ? (
              <Alert role="alert" id={alertId} data-booked="">
                {bookedText}
              </Alert>
            ) : null}
            {error ? (
              <Alert role="alert" data-server-error="">
                {error}
              </Alert>
            ) : null}
            {owned.length ? (
              <Section>
                <SectionTitle>Closed by the owner</SectionTitle>
                <BlockList>
                  {owned.map(b => (
                    <li key={b.id} data-block-id={b.id}>
                      {blockLine(b)}
                    </li>
                  ))}
                </BlockList>
              </Section>
            ) : (
              <Field>
                <Label htmlFor={reasonId}>Reason</Label>
                <Input
                  id={reasonId}
                  name="reason"
                  value={reason}
                  maxLength={REASON_MAX}
                  disabled={booked.length > 0 || saving}
                  aria-describedby={hintId}
                  onChange={event => setReason(event.target.value)}
                  onKeyDown={event => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      void close();
                    }
                  }}
                />
                <FieldHint id={hintId}>Do not write guest names here</FieldHint>
              </Field>
            )}
            {booked.length && !owned.length ? <Reason>Close is not available: a guest is booked in this range.</Reason> : null}
          </div>
        ) : null}
      </FormModal>
      <ConfirmDialog
        open={confirming}
        title="Re-open these dates?"
        message={owned.map(blockLine).join('\n') || 'The closed nights in this range open again.'}
        primaryLabel="Re-open these dates"
        onPrimary={reopen}
        dismissLabel="Keep closed"
        onDismiss={() => setConfirming(false)}
      />
    </>
  );
};
